import { useState, useMemo, useEffect } from 'react'
import { Box, Stack, Collapse, Typography, IconButton } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useIsMobile } from '@/hooks/Responsive'
import { userSend } from 'api/users';
import { cmmCd } from 'api/comp';
import { useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify'

export default function Faq() {
  const { isMobile } = useIsMobile()

  // 탭 카테고리
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('');
  const [openId, setOpenId] = useState(null);
  const [allFaq, setAllFaq] = useState(null);     // FAQ 데이터

  const location = useLocation()

  const categoryMap = useMemo(() => {
    return categories.reduce((acc, cur) => {
      acc[cur.comCd] = cur.comNm
      return acc
    }, {})
  }, [categories])

  const toggle = (id) => {
    setOpenId((prev) => (prev === id ? null : id))
  }

  // 탭 카테고리 조회
  const searchCtgr = async () => {
    const resCtgrCombo = await cmmCd("FAQTYPE");
    const setData = [
      {
        comCd : "",
        comDesc : "",
        comGrpCd: "FAQTYPE",
        comGrpSeq: "0",
        comNm: "전체",
        comVal: null,
        level: null,
        path: null
      },   // 추가할 데이터
      ...resCtgrCombo?.data
    ];
    setCategories(setData);
  };

  const searchFaq = async () => {
    const resData = await userSend('/alarm/getFaqList', {});
    setAllFaq(resData.data);
  };

  // FAQ 카테고리
  useEffect(() => {
    searchCtgr();
  }, []);

  // FAQ 정보 호출
  useEffect(() => {
    searchFaq();
  }, [categories]);

  // 카테고리 필터
  const filteredFaq = useMemo(() => {
    if (activeTab === '') return allFaq
    return allFaq?.filter((f) => f.bbsTypeCode === activeTab)
  }, [activeTab, allFaq])

  // 정화된 faq 사용
  const sanitizedFaq = useMemo(() => {
    if (!filteredFaq) return []

    return filteredFaq.map((faq) => ({
      ...faq,
      safeHtml: DOMPurify.sanitize(faq.bulletinSubstatnce ?? '', {
        USE_PROFILES: { html: true },
      }),
    }))
  }, [filteredFaq])

  useEffect(() => {
    const s = location.state
    if (s?.activeTab !== undefined) setActiveTab(s.activeTab)
    if (s?.openId !== undefined) setOpenId(s.openId)
    // 검색에서 넘어온 state는 1회만 쓰고 싶으면 여기서 replace로 state 제거하는 방식도 가능
  }, [location.state])

  return (
    <Box sx={{ width: '100%' }}>
      {/* 카테고리 탭 */}
      <Stack
        role="group"
        aria-label="FAQ 카테고리"
        direction="row"
        flexWrap="wrap"
        sx={{
          mb: 4,
          gap: 1,
          '& > *': {
            flex: isMobile ? '0 0 calc(33% - 8px)' : '1 1 0',
          },
        }}
      >
        {categories.map((cat) => {
          const selected = activeTab === cat.comCd
          return (
            <Box
              key={cat.comCd}
              component="button"
              type="button"
              aria-pressed={selected}
              onClick={() => {
                setActiveTab(cat.comCd)
                setOpenId(null)
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '56px',
                borderRadius: 2,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                typography: 'body1',
                fontWeight: '600',
                border: selected ? '1px solid' : '1px solid',
                borderColor: selected ? 'grey.800' : 'divider',
                bgcolor: selected ? 'grey.800' : 'common.white',
                color: selected ? 'common.white' : 'grey.600',
                transition: 'all 0.2s ease',
              }}
            >
              {cat.comNm}
            </Box>
          )
        })}
      </Stack>

      {/* FAQ 리스트 */}
      <Stack sx={{ borderTop: '2px solid', borderColor: 'primary.main'}}>
        {/* 데이터 없을 때 */}
        {allFaq !== null && sanitizedFaq.length === 0 && (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 15 }}>
            <ErrorOutlineIcon sx={{ fontSize: '32px', color: 'grey.light' }} />
            <Typography color="grey.500">선택하신 카테고리에는 등록된 FAQ가 없습니다.</Typography>
          </Stack>
        )}

        {sanitizedFaq?.map((faq) => {
          const open = openId === faq.bulletinId
          return (
            <Box
              key={faq.bulletinId}
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
              }}
              onClick={() => toggle(faq.bulletinId)}
            >
              {/* 질문 영역 */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{
                  minHeight: '74px',
                  p: isMobile ? 2 : 3,
                }}
              >
                {/* 왼쪽 Q + 제목 */}
                <Stack direction="row" flexWrap={isMobile ? 'wrap' : 'nowrap'} flex={1} sx={{ gap: 1.5 }}>
                  <Typography component="span" fontWeight={600} color="primary.main">Q</Typography>
                  <Typography component="strong" fontWeight={600} sx={{ width: isMobile ? 'auto' : '96px', textAlign: 'center' }}>{categoryMap[faq.bbsTypeCode] || '기타'}</Typography>
                  <Typography sx={{ width: isMobile ? '100%' : 'auto' }}>
                    {faq.bulletinTitle}
                  </Typography>
                </Stack>

                {/* 우측 화살표 */}
                <IconButton 
                  size="small"
                  aria-label={open ? '답변 접기' : '답변 펼치기'}
                >
                  {open ? (
                    <ExpandLessIcon sx={{ color: 'grey.dark' }} />
                  ) : (
                    <ExpandMoreIcon sx={{ color: 'grey.dark' }} />
                  )}
                </IconButton>
              </Stack>

              {/* 답변 영역 */}
              <Collapse in={open} timeout={260}>
                <Box
                  sx={{
                    py: isMobile ? 2 : 4,
                    px: isMobile ? 2 : 3,
                    bgcolor: 'primary.50',
                  }}
                  dangerouslySetInnerHTML={{ __html: faq.safeHtml }}
                />
                  {/* {faq.bulletinSubstatnce}
                 </Box> */}
              </Collapse>
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}
